import { createContext, useContext, useEffect, useState } from "react";

/**
 * TickProvider — one 1-second interval shared by every component that
 * needs to display a live "last click X seconds ago" label. Cheaper than
 * one setInterval per card, and it pauses when the tab is hidden so we
 * don't waste CPU / battery.
 *
 * Usage: wrap the app once, then call `useNow()` where you need it — the
 * hook returns the current Date.now() timestamp and re-renders the
 * consuming component every second while the tab is visible.
 */
const TickContext = createContext(Date.now());

export function TickProvider({ children }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let timer = null;
    const start = () => {
      if (timer) return;
      // Sync immediately so consumers don't stay on a stale tick after
      // the tab comes back from being hidden.
      setNow(Date.now());
      timer = window.setInterval(() => setNow(Date.now()), 1000);
    };
    const stop = () => {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return <TickContext.Provider value={now}>{children}</TickContext.Provider>;
}

export function useNow() {
  return useContext(TickContext);
}
